import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { AuditListItem } from "@/hooks/use-audits";
import { AUDIT_STATUS_BADGE_VARIANTS, AUDIT_STATUS_LABELS } from "@/lib/audit-status";
import { formatDate } from "@/lib/format";

function ProgressCell({ checked, total }: { checked: number; total: number }) {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <span className="text-sm font-medium tabular-nums">
        {checked} / {total}
      </span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function matches(audit: AuditListItem, query: string): boolean {
  if (!query) return true;
  const haystack = [
    audit.projectName,
    audit.accountName ?? "",
    audit.assignedToName ?? "",
    ...audit.templates.map((t) => t.name),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
}

export function TechPartnerAuditsTable({ audits }: { audits: AuditListItem[] }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();

  const visible = useMemo(
    () => audits.filter((a) => (!status || a.status === status) && matches(a, query.trim())),
    [audits, query, status],
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle>My Project Audits</CardTitle>
          <p className="text-sm text-muted-foreground">
            All project audits where you are named Tech Partner.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search project, client, template or auditor"
              className="pl-8 w-72"
              aria-label="Search audits"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="">All statuses</option>
            {Object.entries(AUDIT_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {audits.length === 0 ? (
          <EmptyState
            title="No audits yet"
            description="Audits on projects where you are named Tech Partner will show up here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y text-left text-xs uppercase tracking-wide text-muted-foreground">
                  {[
                    "Project",
                    "Client",
                    "Template",
                    "Auditor",
                    "Progress",
                    "Status",
                    "Last Updated",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((audit) => (
                  <tr
                    key={audit.id}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate({ to: "/dashboard/audits/$auditId", params: { auditId: audit.id } })
                    }
                  >
                    <td className="px-4 py-3 font-medium">{audit.projectName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{audit.accountName ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {audit.templates.map((t) => t.name).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {audit.assignedToName ?? "Unassigned"}
                    </td>
                    <td className="px-4 py-3">
                      <ProgressCell checked={audit.checkedItems} total={audit.totalItems} />
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={AUDIT_STATUS_BADGE_VARIANTS[audit.status] ?? "outline"}>
                        {AUDIT_STATUS_LABELS[audit.status] ?? audit.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDate(audit.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-4 py-3 text-xs text-muted-foreground">
              Showing {visible.length} of {audits.length} audits
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
